/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import useKlarityServiceTemplate from 'hooks/useKlarityServiceTemplate';
import { KlarityService, ServerMeta, Meta, TKlarityServiceServerTableItem } from 'types/common';
import useGetPPAServiceDetail from 'hooks/useGetPPAServiceDetail';
import { INSURANCE_TYPE_CASH, INSURANCE_TYPE_CASH_AND_INSURANCE, INSURANCE_TYPE_INSURANCE } from 'constants/common';
import { SettingEnum } from 'types/enumerationData';

interface Step2SpecialtySelectionProps {
    allStates?: SettingEnum[] | Meta[];
    onNext: () => void;
    onBack: () => void;
    selectedSpecialties: KlarityService[];
    onSpecialtiesChange: (specialties: KlarityService[]) => void;
    setPaymentMethod: (
        paymentMethod:
            | typeof INSURANCE_TYPE_INSURANCE
            | typeof INSURANCE_TYPE_CASH
            | typeof INSURANCE_TYPE_CASH_AND_INSURANCE,
    ) => void;
    setServerIdStateMap: (serverIdStateMap: Record<string, ServerMeta[]>) => void;
    tableData?: TKlarityServiceServerTableItem[];
}

const Step2SpecialtySelection: React.FC<Step2SpecialtySelectionProps> = ({
    tableData,
    allStates,
    onNext,
    onBack,
    selectedSpecialties: initialSelectedSpecialties,
    onSpecialtiesChange,
    setPaymentMethod,
    setServerIdStateMap,
}) => {
    const [serviceDetail] = useGetPPAServiceDetail();
    const [templateDataState] = useKlarityServiceTemplate();
    const [selectedSpecialties, setSelectedSpecialties] = useState<KlarityService[]>(initialSelectedSpecialties || []);
    const [addedSpecialtyNames, setAddedSpecialtyNames] = useState<string[]>([]);
    const paymentMethod = useMemo(() => serviceDetail?.supportPayType, [serviceDetail]);
    // const selectedSpecialty = useMemo(() => serviceDetail?.selectedSpecialty, [serviceDetail]);
    const prefillState = useMemo(() => serviceDetail?.selectedState || ['CA', 'NY'], [serviceDetail]);

    // 合并recommend和other数据
    const allSpecialties = useMemo(() => {
        if (!templateDataState) return [];
        return [...(templateDataState.recommend || []), ...(templateDataState.other || [])];
    }, [templateDataState]);

    useEffect(() => {
        if (paymentMethod) {
            setPaymentMethod(paymentMethod);
        }
    }, [paymentMethod, setPaymentMethod]);

    // 从tableData中提取所有已添加的Specialty Name
    useEffect(() => {
        if (tableData && tableData.length > 0) {
            const specialtyNames = Array.from(new Set(tableData.map((item) => item.serviceSpecialtyName)));
            setAddedSpecialtyNames(specialtyNames);
        } else {
            setAddedSpecialtyNames([]);
        }
    }, [tableData]);

    // 初始化serverIdStateMap
    useEffect(() => {
        if (allSpecialties.length > 0 && prefillState && allStates) {
            const stateTarget = allStates.filter((state) => prefillState.includes((state as any).dictKey));
            const newServerIdStateMap: Record<string, ServerMeta[]> = {};
            allSpecialties.forEach((specialty) => {
                const id = `${specialty.id}`;
                newServerIdStateMap[id] = stateTarget as unknown as ServerMeta[];
            });
            setServerIdStateMap(newServerIdStateMap);
        }
    }, [allSpecialties, prefillState, setServerIdStateMap, allStates]);

    // 处理specialty选择
    const handleSpecialtyToggle = useCallback(
        (specialty: KlarityService) => {
            const isSelected = selectedSpecialties.some((item) => item.id === specialty.id);
            if (isSelected) {
                setSelectedSpecialties(selectedSpecialties.filter((item) => item.id !== specialty.id));
            } else {
                setSelectedSpecialties([...selectedSpecialties, specialty]);
            }
        },
        [selectedSpecialties],
    );

    // 检查specialty是否已添加
    const isSpecialtyAdded = useCallback(
        (specialty: KlarityService) => {
            return addedSpecialtyNames.includes(specialty.specialtyName);
        },
        [addedSpecialtyNames],
    );

    // 渲染条件标签
    const renderConditionTags = useCallback((conditions: string) => {
        if (!conditions) return null;
        const conditionsArray = conditions.split(',').filter((condition) => condition.trim());
        // const displayConditions = conditionsArray.slice(0, 6);
        // const moreConditionsCount = conditionsArray.length - 6;

        return (
            <>
                {conditionsArray.map((condition, index) => (
                    <div key={index} className={s.conditionBadge}>
                        {condition.trim()}
                    </div>
                ))}
                {/* {moreConditionsCount > 0 && (
                    <Tooltip title={conditionsArray.slice(6).join(', ')}>
                        <div className={`${s.conditionBadge} ${s.moreTag}`}>
                            {moreConditionsCount} more
                        </div>
                    </Tooltip>
                )} */}
            </>
        );
    }, []);

    // 渲染specialty卡片
    const renderSpecialtyCard = useMemo(() => {
        return allSpecialties.map((specialty: KlarityService) => {
            const isSelected = selectedSpecialties.some((item) => item.id === specialty.id);
            const isAdded = isSpecialtyAdded(specialty);
            const conditions = specialty.commonCondition || specialty.providerConditions || '';
            const conditionArray = conditions.split(',').filter((c) => c.trim());

            return (
                <div
                    key={specialty.id}
                    className={`${s.specialtyCard} ${isSelected ? s.selected : ''} ${isAdded ? s.added : ''}`}
                    onClick={() => {
                        // 已添加的Specialty不允许再次选中
                        if (!isAdded) {
                            handleSpecialtyToggle(specialty);
                        }
                    }}
                >
                    <div className={s.cardContent}>
                        <div className={s.iconSection}>
                            <div className={s.iconCircle}>
                                <img src={specialty.iconUri} className={s.icon} alt={specialty.specialtyName} />
                            </div>
                        </div>
                        <div className={s.titleRow}>
                            <div className={s.titleWithBadge}>
                                <h3>{specialty.specialtyName}</h3>
                                <div className={s.badge}>{conditionArray.length} conditions</div>
                            </div>
                            {isAdded ? (
                                <div className={s.addedStatus}>
                                    <CheckCircleOutlined className={s.checkIcon} />
                                    <span>Add</span>
                                </div>
                            ) : isSelected ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M10.0004 19.6004C15.3023 19.6004 19.6004 15.3023 19.6004 10.0004C19.6004 4.69846 15.3023 0.400391 10.0004 0.400391C4.69846 0.400391 0.400391 4.69846 0.400391 10.0004C0.400391 15.3023 4.69846 19.6004 10.0004 19.6004ZM14.4489 8.44892C14.9175 7.98029 14.9175 7.22049 14.4489 6.75186C13.9803 6.28323 13.2205 6.28323 12.7519 6.75186L8.80039 10.7033L7.24892 9.15186C6.78029 8.68323 6.02049 8.68323 5.55186 9.15186C5.08323 9.62049 5.08323 10.3803 5.55186 10.8489L7.95186 13.2489C8.42049 13.7175 9.18029 13.7175 9.64891 13.2489L14.4489 8.44892Z"
                                        fill="#0E62F4"
                                    />
                                </svg>
                            ) : null}
                        </div>
                    </div>
                    {/* eslint-disable-next-line react/no-danger */}
                    <p className={s.description} dangerouslySetInnerHTML={{ __html: specialty.description }} />
                    {/* 只在选中时显示条件标签 */}
                    {isSelected && (
                        <>
                            <div className={s.divider} />
                            <div className={s.conditionsSection}>
                                <div className={s.conditionsTitle}>Conditions covered:</div>
                                <div className={s.conditionsGrid}>{renderConditionTags(conditions)}</div>
                            </div>
                        </>
                    )}
                </div>
            );
        });
    }, [allSpecialties, selectedSpecialties, isSpecialtyAdded, renderConditionTags, handleSpecialtyToggle]);

    // 更新父组件的selectedSpecialties
    useEffect(() => {
        onSpecialtiesChange(selectedSpecialties);
    }, [selectedSpecialties, onSpecialtiesChange]);

    return (
        <div className={s.wrap}>
            {/* 页面上部文字 */}
            <div className={s.header}>
                <h1 className={s.title}>Set up your service templates</h1>
                <p className={s.subtitle}>
                    Please select the service templates that match the conditions you treat, your license, and your
                    practice states. Choose at least one to continue. If you offer multiple specialties, feel free to
                    select all that apply.
                </p>
            </div>

            {/* Specialty卡片列表 */}
            <div className={s.specialtiesContainer}>{renderSpecialtyCard}</div>

            {/* 底部按钮 */}
            <div className={s.footer}>
                <Button onClick={onBack} className={s.backBtn}>
                    Back
                </Button>
                <Button
                    type="primary"
                    onClick={onNext}
                    disabled={selectedSpecialties?.length === 0}
                    className={s.nextBtn}
                >
                    Next
                </Button>
            </div>
        </div>
    );
};

export default Step2SpecialtySelection;
